/**
 * Rasterising sheets (a single page or a two-page spread) for Export / Print — the spread counterpart of
 * renderUtils.renderPageToCanvas / pagesToPdf. Kept apart from lib/spreads.js (pure helpers) because this one renders.
 */
import React from 'react';
import jsPDF from 'jspdf';
import { StaticPage } from '../components/editor/ObjectRenderer';
import { fillToCss } from './colors';
import { renderReactToCanvas, renderPageToCanvas, pageRenderSize, marksEnabled, PrinterMarks, MarkedPage, foldGuidesOf, PUBLICATION_THUMB_SIZE } from './renderUtils';
import { buildSheets } from './spreads';

const masterOf = (masters, page) => (page ? masters?.[page.masterId || 'default'] : null);

/**
 * A spread as it is displayed: left page, right page, white where a half is blank (page 1 / an even last page).
 * `bleed` > 0 lets each page's background and off-page objects run into the bleed on its three outer sides (clipped at
 * the spine), like MarkedPage does for a single page.
 */
export const SpreadSheet = ({ sheet, masters, textFlow = {}, scale = 1, bleed = 0, emptySlot = false }) => {
  const { left, right, spine, width, height } = sheet;
  const W = (width + bleed * 2) * scale, H = (height + bleed * 2) * scale;
  // optional dashed outline where the blank half is (thumbnails mirror the canvas; export / print leave it white)
  const slot = emptySlot && (!left || !right) ? (
    <div style={{ position: 'absolute', left: (bleed + (left ? spine : 0)) * scale, top: bleed * scale, width: (left ? (right || left).width : (right || left).width) * scale, height: height * scale, border: `${Math.max(1, Math.round(2 * scale))}px dashed rgba(120,113,108,0.45)`, borderRadius: 2, boxSizing: 'border-box' }} aria-hidden="true" data-testid="spread-sheet-empty-slot" />
  ) : null;
  const half = (page, x, isLeft) => {
    if (!page) return null;
    const bg = page.background || { type: 'solid', color: '#ffffff' };
    // container: the page plus its bleed on the outer sides only; the inner edge stops exactly at the spine
    const cx = isLeft ? 0 : (bleed + x) * scale;
    const cw = (page.width + bleed) * scale;
    const px = isLeft ? bleed * scale : 0;
    return (
      <div style={{ position: 'absolute', left: cx, top: 0, width: cw, height: H, overflow: 'hidden', background: bleed ? fillToCss(bg, '#ffffff') : 'transparent' }} data-testid={isLeft ? 'spread-sheet-left' : 'spread-sheet-right'}>
        <div style={{ position: 'absolute', left: px, top: bleed * scale, width: page.width * scale, height: page.height * scale }}>
          <StaticPage page={page} master={masterOf(masters, page)} scale={scale} textFlow={textFlow} showBackground={!bleed} style={bleed ? { overflow: 'visible' } : undefined} />
        </div>
      </div>
    );
  };
  return (
    <div style={{ position: 'relative', width: W, height: H, background: '#ffffff', overflow: 'hidden' }} data-testid="spread-sheet" data-pages={sheet.pageIndexes.map((i) => i + 1).join('-')}>
      {half(left, 0, true)}
      {half(right, spine, false)}
      {slot}
    </div>
  );
};

/** True when a publication's lists / thumbnails should show its opening spread rather than page 1 alone. */
export const showsAsSpread = (doc) => (doc?.layoutMode === 'spread' || doc?.layout_mode === 'spread') && (doc?.pages?.length || 0) > 1;

/** The opening sheet of a publication as it is first displayed: page 1 alone (single view) or page 1 on the right of its spread. */
export const openingSheet = (doc) => {
  const pages = doc?.pages || [];
  if (!pages.length) return null;
  return showsAsSpread(doc) ? buildSheets(pages, [0], 'spread')[0] : buildSheets(pages, [0], 'single')[0];
};

/**
 * Small JPEG data URL for the Home / Open lists, captured on every cloud save: page 1 — or, when the publication is in
 * Two-Page Spread view, its opening spread (page 1 on the right, the empty half outlined) so a booklet looks like one.
 */
export const capturePublicationThumbnail = async (doc) => {
  const sheet = openingSheet(doc);
  if (!sheet) return undefined;
  try {
    const scale = Math.min(1, PUBLICATION_THUMB_SIZE / Math.max(sheet.width || 816, sheet.height || 1056));
    const masters = doc.masters || {};
    const textFlow = doc.textFlow || {};
    if (sheet.kind === 'page') {
      const canvas = await renderPageToCanvas(sheet.page, masters[sheet.page.masterId || 'default'], textFlow, scale);
      return canvas.toDataURL('image/jpeg', 0.8);
    }
    const canvas = await renderReactToCanvas(<SpreadSheet sheet={sheet} masters={masters} textFlow={textFlow} scale={1} emptySlot />, sheet.width, sheet.height, { scale });
    return canvas.toDataURL('image/jpeg', 0.8);
  } catch (e) {
    console.warn('[thumbnail] render failed', e?.message || e); // eslint-disable-line no-console
    return undefined;
  }
};

/**
 * Printer's marks for a spread: the publication's fold guides apply to each page (vertical ones shifted on to the
 * right-hand page), plus a fold at the spine when fold marks are on.
 */
export const spreadGuides = (sheet, guides = []) => {
  const folds = foldGuidesOf(guides);
  const out = [];
  const seen = new Set();
  const push = (g) => { const k = `${g.orientation}:${Math.round(Number(g.position))}`; if (!seen.has(k)) { seen.add(k); out.push(g); } };
  folds.forEach((g) => {
    if (g.orientation === 'horizontal') push({ ...g });
    else {
      if (sheet.left) push({ ...g });
      if (sheet.right) push({ ...g, position: Number(g.position) + sheet.spine });
    }
  });
  push({ kind: 'fold', orientation: 'vertical', position: sheet.spine, id: 'spine' });
  return out;
};

/** The spread inside its bleed with crop / fold marks around it (what Export / Print rasterise when marks are on). */
export const MarkedSpread = ({ sheet, masters, textFlow = {}, marks, guides = [], scale = 1 }) => {
  const { bleed } = pageRenderSize(sheet, marks);
  if (!bleed) return <SpreadSheet sheet={sheet} masters={masters} textFlow={textFlow} scale={scale} />;
  return (
    <div style={{ position: 'relative', width: (sheet.width + bleed * 2) * scale, height: (sheet.height + bleed * 2) * scale, background: '#ffffff', overflow: 'hidden' }} data-testid="marked-spread" data-bleed={bleed}>
      <SpreadSheet sheet={sheet} masters={masters} textFlow={textFlow} scale={scale} bleed={bleed} />
      <PrinterMarks w={sheet.width} h={sheet.height} bleed={bleed} marks={marks} guides={spreadGuides(sheet, guides)} scale={scale} />
    </div>
  );
};

/** Preview-friendly element for a sheet (page or spread), with or without marks. */
export const SheetView = ({ sheet, masters, textFlow = {}, marks = null, guides = [], scale = 1 }) => {
  if (sheet.kind === 'page') {
    const master = masterOf(masters, sheet.page);
    if (marksEnabled(marks)) return <MarkedPage page={sheet.page} master={master} textFlow={textFlow} marks={marks} guides={guides} scale={scale} />;
    return <StaticPage page={sheet.page} master={master} scale={scale} textFlow={textFlow} />;
  }
  return marksEnabled(marks)
    ? <MarkedSpread sheet={sheet} masters={masters} textFlow={textFlow} marks={marks} guides={guides} scale={scale} />
    : <SpreadSheet sheet={sheet} masters={masters} textFlow={textFlow} scale={scale} />;
};

/** Rasterise a sheet. Single-page sheets go through renderPageToCanvas (identical output to before). */
export const renderSheetToCanvas = (sheet, masters = {}, textFlow = {}, scale = 2, marks = null) => {
  if (sheet.kind === 'page') return renderPageToCanvas(sheet.page, masterOf(masters, sheet.page), textFlow, scale, marks);
  const { width, height } = pageRenderSize(sheet, marks);
  const el = marksEnabled(marks)
    ? <MarkedSpread sheet={sheet} masters={masters} textFlow={textFlow} marks={marks} guides={marks.guides || []} scale={1} />
    : <SpreadSheet sheet={sheet} masters={masters} textFlow={textFlow} scale={1} />;
  return renderReactToCanvas(el, width, height, { scale });
};

const PX_TO_MM = 25.4 / 96;

/** Multi-page PDF, one PDF page per sheet (a spread becomes one wide page). Returns a jsPDF instance. */
export const sheetsToPdf = async (sheets, masters = {}, textFlow = {}, { scale = 2, onProgress, marks = null } = {}) => {
  const size = (s) => pageRenderSize(s, marks);
  const first = size(sheets[0]);
  const pdf = new jsPDF({ orientation: first.width > first.height ? 'landscape' : 'portrait', unit: 'mm', format: [first.width * PX_TO_MM, first.height * PX_TO_MM] });
  for (let i = 0; i < sheets.length; i++) {
    const sz = size(sheets[i]);
    onProgress && onProgress(i + 1, sheets.length);
    if (i > 0) pdf.addPage([sz.width * PX_TO_MM, sz.height * PX_TO_MM], sz.width > sz.height ? 'landscape' : 'portrait');
    const canvas = await renderSheetToCanvas(sheets[i], masters, textFlow, scale, marks);
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, sz.width * PX_TO_MM, sz.height * PX_TO_MM);
  }
  return pdf;
};
